import { useMemo } from 'react';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { ProductChecklistStatisticsResult } from '@mews-commander/data-access';
import { useLocalizeProperty, useLocalizeText } from '@mews/framework';
import { UnicodeEntity } from '@mews/enums';
import { TextKey } from '@mews-commander/enums';
import { ProgressCharts } from '@mews/b2b-ui';
import { sortByAgeCategoryOrd } from '@mews-commander/legacy-entities/src/ducks/ageCategories';
import { NO_AGE_CATEGORY_ID } from '../../utils/ageCategories';
import { useAgeCategoriesContext } from '../../contexts/AgeCategoriesContext';

enum NormalisedStackedBarChartDataTestIds {
    legendCategoryValue = 'nsbc-category-value',
    legendCategoryMax = 'nsbc-category-max',
}

export const useClaimedByAgeChartDataCategories = (
    data: ProductChecklistStatisticsResult | undefined
): ProgressCharts[] => {
    const { sortedAgeCategories, ageCategoriesById } = useAgeCategoriesContext();
    const getLocalizedProperty = useLocalizeProperty();
    const lt = useLocalizeText();

    return useMemo(() => {
        if (!data) {
            return [];
        }

        return pipe(
            data.statisticsData.claimedByAge,
            array.sort(sortByAgeCategoryOrd(sortedAgeCategories)),
            array.mapWithIndex((index, { ageCategoryId, claimedOrderItemCount, orderItemCount }) => {
                const ageCategory = ageCategoryId ? ageCategoriesById?.[ageCategoryId] : null;
                const label = ageCategory ? getLocalizedProperty(ageCategory.name) : lt({ textKey: TextKey.None });

                const id = ageCategoryId ?? NO_AGE_CATEGORY_ID;

                return {
                    id,
                    title: label,
                    total: orderItemCount,
                    data: [
                        {
                            name: label,
                            value: claimedOrderItemCount,
                            color: 'green',
                            id: `${index}-${claimedOrderItemCount}-${id}`,
                        },
                    ],
                    score: {
                        value: (
                            <span data-test-category-id={id}>
                                <span data-test-id={NormalisedStackedBarChartDataTestIds.legendCategoryValue}>
                                    {claimedOrderItemCount}
                                </span>
                                {UnicodeEntity.ForwardSlash}
                                <span data-test-id={NormalisedStackedBarChartDataTestIds.legendCategoryMax}>
                                    {orderItemCount}
                                </span>
                            </span>
                        ),
                    },
                };
            })
        );
    }, [data, sortedAgeCategories, ageCategoriesById, getLocalizedProperty, lt]);
};
